import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, ShieldCheck, CheckCircle2, Building, Users, Calendar, Award, ArrowRight } from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/MandatoryDisclosure.scss';

export default function MandatoryDisclosure() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="mandatory-disclosure-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Mandatory Public Disclosure - Prabhat Tara Vidya Niketan</title>
        <meta
          name="description"
          content="Mandatory Public Disclosure as per CBSE & State Board norms for Prabhat Tara Vidya Niketan, Rajkhand, Goraul, Vaishali, Bihar."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/mandatory-disclosure" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-inner">
          <span className="badge">
            <ShieldCheck size={16} /> Official Compliance & Transparency
          </span>
          <h1>Mandatory Public Disclosure</h1>
          <p>
            In accordance with educational board guidelines (CBSE / State Government Regulations), Prabhat Tara Vidya Niketan publishes all general, academic, safety, and infrastructure disclosures below.
          </p>
        </div>
      </div>

      <div className="disclosure-content-wrapper">
        <div className="disclosure-card">
          
          {/* SECTION A: General Information */}
          <section className="disclosure-section">
            <h2>
              <FileText size={22} color="#ff6b2b" /> A. General Information
            </h2>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Information</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sno">1</td>
                    <td>Name of the School</td>
                    <td style={{ fontWeight: '700', color: '#011673' }}>Prabhat Tara Vidya Niketan</td>
                  </tr>
                  <tr>
                    <td className="sno">2</td>
                    <td>Affiliation Status & Level</td>
                    <td>Affiliated to CBSE / Registered with Bihar Government (Nursery to Secondary)</td>
                  </tr>
                  <tr>
                    <td className="sno">3</td>
                    <td>School Code / U-DISE</td>
                    <td>Available at School Office</td>
                  </tr>
                  <tr>
                    <td className="sno">4</td>
                    <td>Complete Address with PIN Code</td>
                    <td>Rajkhand, Block - Goraul, District - Vaishali, Bihar, PIN - 844118</td>
                  </tr>
                  <tr>
                    <td className="sno">5</td>
                    <td>Principal Name & Qualification</td>
                    <td>Mr. Kishan (M.A., B.Ed.)</td>
                  </tr>
                  <tr>
                    <td className="sno">6</td>
                    <td>School Email ID</td>
                    <td><a href="mailto:principal@prabhatvidyanikethan.com" style={{ color: '#011673', textDecoration: 'underline' }}>principal@prabhatvidyanikethan.com</a></td>
                  </tr>
                  <tr>
                    <td className="sno">7</td>
                    <td>Contact Telephone / Mobile Numbers</td>
                    <td>+91 9939988606, +91 7654164726</td>
                  </tr>
                  <tr>
                    <td className="sno">8</td>
                    <td>Year of Establishment</td>
                    <td>1995 (30+ Years of Service)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION B: Documents and Information */}
          <section className="disclosure-section">
            <h2>
              <Award size={22} color="#27ae60" /> B. Documents and Information
            </h2>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Documents / Certificates</th>
                    <th>Verification Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sno">1</td>
                    <td>Affiliation / Upgradation Letter & Recent Extension</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Certified & Verified</span></td>
                  </tr>
                  <tr>
                    <td className="sno">2</td>
                    <td>Society / Trust / Company Registration Certificate</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Registered under Societies Act</span></td>
                  </tr>
                  <tr>
                    <td className="sno">3</td>
                    <td>No Objection Certificate (NOC) Issued by State Govt.</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Issued by Govt. of Bihar</span></td>
                  </tr>
                  <tr>
                    <td className="sno">4</td>
                    <td>Recognition Certificate under RTE Act 2009</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Valid & Recognized</span></td>
                  </tr>
                  <tr>
                    <td className="sno">5</td>
                    <td>Building Safety Certificate as per National Building Code</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Certified by Competent Engineer</span></td>
                  </tr>
                  <tr>
                    <td className="sno">6</td>
                    <td>Fire Safety Certificate issued by Competent Authority</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Fire Safety Compliant</span></td>
                  </tr>
                  <tr>
                    <td className="sno">7</td>
                    <td>Safe Drinking Water and Sanitary Condition Certificate</td>
                    <td><span className="status-verified"><CheckCircle2 size={16} /> Tested Safe & Potable RO Water</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION C: Result and Academics */}
          <section className="disclosure-section">
            <h2>
              <Calendar size={22} color="#0c278c" /> C. Result & Academics
            </h2>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Information</th>
                    <th>Details / Web Link</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sno">1</td>
                    <td>Fee Structure of the School</td>
                    <td><a href="/fee-structure" className="table-link">View Class-wise Fee Structure <ArrowRight size={14} /></a></td>
                  </tr>
                  <tr>
                    <td className="sno">2</td>
                    <td>Annual Academic Calendar</td>
                    <td><a href="/academics#academic-calendar" className="table-link">View Academic Calendar <ArrowRight size={14} /></a></td>
                  </tr>
                  <tr>
                    <td className="sno">3</td>
                    <td>List of School Management Committee (SMC)</td>
                    <td><a href="/about-us#management-committee" className="table-link">View SMC List <ArrowRight size={14} /></a></td>
                  </tr>
                  <tr>
                    <td className="sno">4</td>
                    <td>List of Parents Teachers Association (PTA) Members</td>
                    <td>Constituted Annually (Available in School Records)</td>
                  </tr>
                  <tr>
                    <td className="sno">5</td>
                    <td>Secondary (Class X) Board Exam Result Track Record</td>
                    <td><span className="status-verified" style={{ color: '#16a34a' }}>100% Pass Percentage with Distinction</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION D: Staff (Teaching) */}
          <section className="disclosure-section">
            <h2>
              <Users size={22} color="#f0a500" /> D. Staff (Teaching & Academic)
            </h2>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Designation / Parameter</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sno">1</td>
                    <td>Principal</td>
                    <td style={{ fontWeight: '700' }}>01</td>
                  </tr>
                  <tr>
                    <td className="sno">2</td>
                    <td>Total Number of Qualified Teachers</td>
                    <td style={{ fontWeight: '700' }}>50+</td>
                  </tr>
                  <tr>
                    <td className="sno">3</td>
                    <td>Teacher-to-Section Ratio</td>
                    <td>1 : 1.5</td>
                  </tr>
                  <tr>
                    <td className="sno">4</td>
                    <td>Details of Special Educator & Student Counsellor</td>
                    <td>Available on Campus</td>
                  </tr>
                  <tr>
                    <td className="sno">5</td>
                    <td>Teaching Staff Profiles & Qualifications</td>
                    <td><a href="/teaching-staff" className="table-link">View Faculty Directory <ArrowRight size={14} /></a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION E: School Infrastructure */}
          <section className="disclosure-section">
            <h2>
              <Building size={22} color="#0c278c" /> E. School Infrastructure Details
            </h2>
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Infrastructure Facility</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sno">1</td>
                    <td>Total Campus Area (in Square Meters)</td>
                    <td style={{ fontWeight: '700' }}>8,093+ Sq. Mtrs. (Spacious Green Campus)</td>
                  </tr>
                  <tr>
                    <td className="sno">2</td>
                    <td>Number of Classrooms (Well-ventilated & lit)</td>
                    <td>35+ Classrooms (500+ sq. ft each)</td>
                  </tr>
                  <tr>
                    <td className="sno">3</td>
                    <td>Laboratories (Composite Science Lab & Computer Lab)</td>
                    <td>2 Fully Equipped Labs (600+ sq. ft each)</td>
                  </tr>
                  <tr>
                    <td className="sno">4</td>
                    <td>Library Facility & Reading Room</td>
                    <td>1 Library with 3,500+ books & periodicals</td>
                  </tr>
                  <tr>
                    <td className="sno">5</td>
                    <td>High-Speed Internet & Digital Facility</td>
                    <td><span className="status-verified">Yes (100 Mbps Wi-Fi & LAN)</span></td>
                  </tr>
                  <tr>
                    <td className="sno">6</td>
                    <td>Separate Toilets for Boys and Girls</td>
                    <td><span className="status-verified">Yes (Clean & Hygienic blocks)</span></td>
                  </tr>
                  <tr>
                    <td className="sno">7</td>
                    <td>CCTV Surveillance & Safety Measures</td>
                    <td><span className="status-verified">Comprehensive 24/7 CCTV coverage</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <div className="contact-notice-box">
            <div className="notice-copy">
              <strong>Need Further Information or Verified Physical Copies?</strong>
              <span>Parents and guardians are welcome to inspect certificates at the administrative office during working hours.</span>
            </div>
            <a href="/contact-us" className="btn-notice-contact">
              <span>Contact School Office</span>
              <ArrowRight size={16} />
            </a>
          </div>

        </div>
      </div>
    </div>
  );
}
